import { ref } from 'vue';
import messagingApi from '@/common/api/messaging.api';

/**
 * The live message stream: a WebSocket to bn-sock, the shared BestNotes realtime gateway (#5338,
 * moved off the Mercure SSE hub in #5432).
 *
 * Server->client only in practice, which is the right shape — sending a message is already a POST,
 * and the gateway accepts and ignores inbound frames.
 *
 * **Events carry no message content.** The server publishes `{ type, threadId, messageId }` and
 * nothing else; subscribers re-read through the normal Aidbox path, where
 * `messaging-communication-read` gates on `recipient = {{user.practitioner.id}}`. That predicate is
 * invitation-forward visibility (#5339), so an event carrying a body would deliver content no
 * policy had evaluated. Do not "optimize" the re-read away.
 *
 * **The token is in the query string, and that is a REGRESSION we accepted rather than chose.** The
 * SSE version of this file argued against exactly this, and the argument was right: a credential in a
 * URL lands in access logs and in every proxy in between. It stands as written — what changed is that
 * it is no longer avoidable. The browser `WebSocket` constructor takes a URL and a subprotocol list
 * and nothing else, so it cannot send an `Authorization` header, and bn-sock reads the token only
 * from `?token=` or that header. The two workarounds are still both wrong: a cookie would be a
 * third-party cookie in every environment (Firebase hosting vs the gateway's own host) and subject to
 * browser blocking, and smuggling a JWT through `Sec-WebSocket-Protocol` is not something the gateway
 * accepts. So it goes in the URL, and the exposure is bounded instead of eliminated: the token lives
 * 120s (down from 900s), names ONE channel, cannot publish, is re-minted per attempt, and only ever
 * travels over wss://.
 *
 * **There is no `fatal` short-circuit any more, and there cannot be.** The SSE reader could see a 401
 * or 403 on the response and stop retrying, because a refused subscription is refused, not
 * unavailable. A browser WebSocket exposes no HTTP status at all — a handshake the gateway answers
 * with 401 surfaces as close code 1006 and nothing else, indistinguishable from a network drop. So a
 * refused connection now retries with backoff like any other failure, which means a genuinely
 * misconfigured environment retries quietly forever rather than stopping. Nothing is shown to the
 * user either way; real time is an enhancement, and its absence is the pre-stream behavior.
 *
 * State is module-scoped so the inbox and an open thread share ONE connection rather than opening a
 * socket each.
 */

const listeners = new Set();
const connected = ref(false);

let socket = null;
let running = false;
let retryDelayMs = 1000;

/**
 * Whether this loop has ever been live, which is what distinguishes a reconnect from a first
 * connect. Only a RE-connect emits `stream.resumed`: on a first connect every consumer has just
 * mounted and read, so telling them to read again would be a duplicate request on every page load.
 */
let hasConnected = false;

/**
 * Bumped every time a connection loop starts or stops.
 *
 * Without it, a loop that is stopping can clobber a loop that has just started: `stop()` sets
 * `running = false` synchronously, but the old loop only notices when its socket closes, and its
 * cleanup would then switch off a successor that began in between. Each loop only touches shared
 * state while it is still the current generation.
 */
let generation = 0;

const MAX_RETRY_MS = 30000;

const isGeneration = (myGeneration) => running && generation === myGeneration;

/**
 * Where the connect token comes from (#5333).
 *
 * Module-level and replaceable, because the connection it feeds is module-level too: a browser
 * session is either the EHR or the portal, never both, so there is exactly one right answer at a
 * time and no reason for two connections. The portal passes its own endpoint (a different firewall
 * and a different channel), and nothing else about the connection logic differs — which is the whole
 * point of parameterizing this rather than writing a second stream.
 *
 * Only the SOURCE is swapped. The channel still comes from the server, baked into the signed token,
 * and is never chosen here.
 */
let getToken = () => messagingApi.getStreamToken();

const emit = (payload) => {
    // One bad listener must not take down the stream for the others.
    listeners.forEach((listener) => {
        try {
            listener(payload);
        } catch (err) {
            console.error('useMessageStream: listener threw', err);
        }
    });
};

/**
 * One connection attempt, resolving when the socket closes for any reason.
 *
 * A fresh token is minted per attempt rather than cached: connect tokens are short-lived by design
 * (the gateway authorizes once, at the upgrade, and never re-evaluates), so re-minting on every
 * reconnect is what keeps expiry from being a special case.
 */
const connectOnce = async (myGeneration) => {
    const { gatewayUrl, token } = await getToken();
    if (!gatewayUrl || !token) throw new Error('Incomplete stream credentials.');

    // Minting is async, so a stop() may have landed while we were waiting. Opening a socket now
    // would leave one nothing is going to close.
    if (!isGeneration(myGeneration)) return;

    const thisSocket = new WebSocket(`${gatewayUrl}?token=${encodeURIComponent(token)}`);
    socket = thisSocket;

    await new Promise((resolve) => {
        thisSocket.onopen = () => {
            // The upgrade was accepted, which is as much as `open` tells us — the channel join is
            // confirmed separately, by the `ready` frame below.
            retryDelayMs = 1000;
        };

        thisSocket.onmessage = (event) => {
            let payload;
            try {
                payload = JSON.parse(event.data);
            } catch {
                // A frame we cannot parse is not worth dropping the connection over.
                console.error('useMessageStream: unparseable event payload');
                return;
            }

            // The gateway's own join confirmation, not an application event — it is where push
            // actually becomes live. Never forwarded: a consumer switching on `type` would have to
            // learn to ignore a frame that says nothing about messaging.
            if (payload?.type === 'ready') {
                connected.value = true;

                // A drop may have spanned events nobody was listening for, and the gateway replays
                // nothing (it holds no durable state; it closes every socket at 55 minutes by
                // design). Rather than a `since` cursor, the reconnect says only "you may have
                // missed something" and each consumer re-reads — which is what every real event
                // already causes, because the payload is content-free. Replaying N missed
                // notifications-to-re-read is observably identical to re-reading once, and strictly
                // more correct for an edit that happened while we were away.
                if (hasConnected) emit({ type: 'stream.resumed' });
                hasConnected = true;
                return;
            }

            emit(payload);
        };

        // A WebSocket error event carries no detail by specification, and `close` always follows it.
        // Everything that reacts to a failure therefore lives in onclose, once.
        thisSocket.onerror = () => {};

        // ANY close is retryable, including a clean 1000/'lifespan' — that one is the gateway
        // handing the socket off before its platform's request cap cuts it, so treating a clean
        // close as "we are done here" would end the stream every 55 minutes.
        thisSocket.onclose = () => resolve();
    });

    if (socket === thisSocket) socket = null;
};

const runLoop = async (myGeneration) => {
    while (isGeneration(myGeneration)) {
        try {
            await connectOnce(myGeneration);
        } catch (err) {
            console.error('useMessageStream: stream dropped, retrying', err);
        }

        if (!isGeneration(myGeneration)) break;
        connected.value = false;

        // Backoff, so a gateway outage does not turn every client into a retry loop.
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
        retryDelayMs = Math.min(retryDelayMs * 2, MAX_RETRY_MS);
    }

    // Only the current loop may declare the stream stopped.
    if (generation === myGeneration) {
        running = false;
        connected.value = false;
    }
};

/**
 * @param {{ tokenSource?: () => Promise<{ gatewayUrl: string, channel: string, token: string }> }} [options]
 *        `tokenSource` replaces where the connect token is minted — the portal's own endpoint
 *        (#5333). Applied on the call rather than on `onMessage`, so it does not matter whether the
 *        layout or the page reaches this composable first.
 */
export function useMessageStream({ tokenSource } = {}) {
    if (tokenSource) getToken = tokenSource;

    /**
     * Subscribe to live events. Returns an unsubscribe function.
     *
     * Registering a listener is what starts the shared connection, and the last listener leaving is
     * what stops it — so a user who never opens messaging never holds a socket open.
     */
    const onMessage = (listener) => {
        listeners.add(listener);

        if (!running) {
            running = true;
            retryDelayMs = 1000;
            // A fresh start is a first connect, not a resume: whoever just subscribed is about to
            // read anyway.
            hasConnected = false;
            generation += 1;
            runLoop(generation);
        }

        return () => {
            listeners.delete(listener);
            if (listeners.size === 0) stop();
        };
    };

    const stop = () => {
        running = false;
        // Invalidates any in-flight loop, so its cleanup cannot switch off a later one.
        generation += 1;
        socket?.close();
        socket = null;
        connected.value = false;
    };

    return { onMessage, stop, connected };
}
